import { Component, OnInit, OnDestroy, signal, inject, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { CustomerService, Customer as CustomerModel } from '../../../services/customer';
import { AdminHeader } from '../../../services/admin-header';
import { ModalService } from '../../../services/modal.service';

@Component({
	selector: 'app-customers',
	imports: [CommonModule, ReactiveFormsModule, FormsModule],
	templateUrl: './customers.html',
	styleUrl: './customers.css',
})
export class Customers implements OnInit, OnDestroy {
	private customerService = inject(CustomerService);
	private fb = inject(FormBuilder);
	private adminHeader = inject(AdminHeader);
	private modalService = inject(ModalService);

	@ViewChild('detailsModalTemplate') detailsModalTemplate!: TemplateRef<any>;

	customers = signal<CustomerModel[]>([]);
	isLoading = signal(true);
	showModal = signal(false);
	isEditMode = signal(false);
	isSubmitting = signal(false);
	isLoadingDetails = signal(false);
	errorMessage = signal('');
	selectedCustomer = signal<CustomerModel | null>(null);
	searchQuery = signal('');
	currentPage = signal(1);
	totalPages = signal(1);

	customerForm: FormGroup = this.fb.group({
		first_name: ['', Validators.required],
		last_name: ['', Validators.required],
		email: ['', Validators.email],
		phone: ['', Validators.required],
		notes: [''],
	});

	private searchTimeout: any;

	ngOnInit() {
		this.loadCustomers();
		this.adminHeader.setHeaderAction({
			label: 'Add Customer',
			icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
			callback: () => this.openModal(),
		});
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderAction();
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
	}

	loadCustomers(page: number = 1) {
		this.isLoading.set(true);
		this.customerService.getAll(this.searchQuery(), page).subscribe({
			next: (res) => {
				if (res.status) {
					this.customers.set(res.data.data);
					this.currentPage.set(res.data.current_page);
					this.totalPages.set(res.data.last_page);
				}
				this.isLoading.set(false);
			},
			error: () => this.isLoading.set(false),
		});
	}

	onSearch(event: Event) {
		const query = (event.target as HTMLInputElement).value;
		this.searchQuery.set(query);
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => this.loadCustomers(), 400);
	}

	openModal(customer?: CustomerModel) {
		if (customer) {
			this.isEditMode.set(true);
			this.selectedCustomer.set(customer);
			this.customerForm.patchValue(customer);
		} else {
			this.isEditMode.set(false);
			this.selectedCustomer.set(null);
			this.customerForm.reset();
		}
		this.showModal.set(true);
	}

	closeModal() {
		this.showModal.set(false);
		this.customerForm.reset();
		this.errorMessage.set('');
	}

	viewDetails(customer: CustomerModel) {
		this.isLoadingDetails.set(true);
		this.modalService.open(this.detailsModalTemplate, '700px', () => {
			this.selectedCustomer.set(null);
		});
		this.customerService.getWithAppointments(customer.id).subscribe({
			next: (res) => {
				if (res.status) {
					this.selectedCustomer.set(res.data);
				}
				this.isLoadingDetails.set(false);
			},
			error: () => {
				this.isLoadingDetails.set(false);
				this.modalService.close();
				alert('Failed to load customer details');
			},
		});
	}

	closeDetailsModal() {
		this.modalService.close();
	}

	saveCustomer() {
		if (this.customerForm.invalid) return;

		this.isSubmitting.set(true);
		const data = this.customerForm.value;

		const request = this.isEditMode()
			? this.customerService.update(this.selectedCustomer()!.id, data)
			: this.customerService.create(data);

		request.subscribe({
			next: () => {
				this.loadCustomers();
				this.closeModal();
				this.isSubmitting.set(false);
			},
			error: (err) => {
				this.errorMessage.set(err.error?.message || 'Failed to save customer');
				this.isSubmitting.set(false);
			},
		});
	}

	changePage(page: number) {
		if (page >= 1 && page <= this.totalPages()) {
			this.loadCustomers(page);
		}
	}

	getStatusColor(status: string): string {
		switch (status) {
			case 'completed': return 'bg-green-100 text-green-800';
			case 'confirmed': return 'bg-blue-100 text-blue-800';
			case 'pending': return 'bg-yellow-100 text-yellow-800';
			case 'cancelled': return 'bg-red-100 text-red-800';
			default: return 'bg-gray-100 text-gray-800';
		}
	}
}
